import { api } from '~/lib/api';
import { toast } from 'sonner';
import type { Endpoint } from '~/types/axios';
import { DEFAULLT_PAGE_SIZE, ROLES } from '~/constant';
import type { UseInfiniteQueryOptionsType, UseQueryOptionsType } from '~/types/css-type';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useSwal } from './use-swal';
import { useAuth } from './use-auth';
import { useModal } from './use-modal';
import { useCommon } from './use-common';

interface CrudApiPayload {
	secret_key?: string;
	token?: string;
	id_staff_action?: number;
	[key: string]: any;
}

// Định nghĩa kiểu dữ liệu cho `options` của useCrud hook
interface UseCrudOptions {
	endpoint?: Endpoint | string;
	id?: number | string;
	id_staff?: number | string;
	listUrl?: string;
	detailUrl?: string;
	createUrl?: string;
	updateUrl?: string;
	deleteUrl?: string;
	[key: string]: any;
}

interface MutationPayload extends CrudApiPayload {
	_customUrl?: string;
	_customUrlSegment?: string;
	_customMessage?: string;
	_hideMessage?: boolean;
	_closeModal?: boolean;
	_queryKey?: any[];
}

export const useCrud = <T>(resource: any[], options?: UseCrudOptions, queryOptions?: UseQueryOptionsType) => {
	const { closeModal } = useModal();
	const queryClient = useQueryClient();
	const { user } = useAuth();
	const { currentSecretkey } = useCommon();
	const endpoint = options?.endpoint ?? undefined;
	const { confirm, loading, close } = useSwal();

	const baseData: CrudApiPayload = useMemo(() => {
		const isAgent = user?.role === ROLES.AGENT;
		return {
			...options,
			endpoint: undefined,
			secret_key: user?.secret_key || currentSecretkey,
			token: user?.token,
			id_staff_action: isAgent ? 0 : user?.id || 0,
			...(isAgent
				? { id: options?.id || user?.id, id_agent: options?.id_agent || user?.id }
				: { id_staff: options?.id_staff || user?.id }),
		};
	}, [user, currentSecretkey, options]);
	const resourcePath = `/${resource?.[0]?.replace(/^\//, '')}`;

	const getAll = () => {
		// biome-ignore lint/correctness/useHookAtTopLevel: <explanation>
		return useQuery({
			queryKey: resource,
			queryFn: () =>
				api.post<T[]>(
					options?.listUrl ? formatUrl(resourcePath, options.listUrl) : resourcePath,
					baseData,
					endpoint,
				),
			...queryOptions,
		});
	};

	const getInfinite = () => {
		// biome-ignore lint/correctness/useHookAtTopLevel: <explanation>
		const queryResult = useInfiniteQuery({
			queryKey: resource,
			queryFn: ({ pageParam }) => api.post<T[]>(resourcePath, { ...baseData, page_num: pageParam }, endpoint),
			initialPageParam: 1,
			getNextPageParam: (lastPage: any, allPages) => {
				const items = lastPage?.list || lastPage;
				return items?.length < DEFAULLT_PAGE_SIZE || lastPage?.total === 0 ? undefined : allPages.length + 1;
			},
			...(queryOptions as UseInfiniteQueryOptionsType),
			placeholderData: keepPreviousData,
		});

		const allPages = (queryResult.data as any)?.pages || [];
		const listData = allPages.every((p: any) => !p?.error_code)
			? allPages.flatMap((page: any) => page?.list || page)
			: [];

		return {
			...queryResult,
			listData,
			total: allPages[0]?.total,
		};
	};

	const getOne = () =>
		// biome-ignore lint/correctness/useHookAtTopLevel: <explanation>
		useQuery({
			queryKey: resource,
			queryFn: () =>
				api.post<T[]>(
					options?.detailUrl ? formatUrl(resourcePath, options.detailUrl) : resourcePath,
					baseData,
					endpoint,
				),
			...queryOptions,
		});

	const formData = (data: any) => ({
		...baseData,
		...data,
		info: undefined,
		limit: undefined,
		page: undefined,
		page_num: undefined,
		page_size: undefined,
		from_date: undefined,
		to_date: undefined,
		id_agent_level: undefined,
		// id_agent_status: undefined,
		// period_name: undefined,
	});

	const cleanPayload = (data: any) => {
		const { info, limit, page, page_num, page_size, from_date, to_date, id_agent_level, ...rest } = data;
		return formData(rest);
	};

	const parseMutationPayload = (payload: MutationPayload, action: string) => {
		const {
			_customUrl,
			_customUrlSegment,
			_customMessage,
			_closeModal,
			_hideMessage,
			_queryKey,
			_customEndpoint,
			...rest
		} = payload as any;

		return {
			url:
				_customUrl ||
				formatUrl(resourcePath, _customUrlSegment || (options as any)?.[`${action}Url`] || action),
			data: cleanPayload(rest),
			meta: { _customMessage, _closeModal, _hideMessage, _queryKey, _customEndpoint },
		};
	};

	const handleSuccess = (data: any, variables: MutationPayload, defaultMessage: string) => {
		if (!data.error_code) {
			if (!variables._hideMessage) {
				toast.success(variables._customMessage || defaultMessage);
			}
			if (variables._closeModal !== false) {
				closeModal();
			}
			queryClient.invalidateQueries({
				queryKey: variables._queryKey || resource,
			});
		}
	};

	const createMutationSource = useMutation({
		mutationFn: (payload: MutationPayload) => {
			const { url, data } = parseMutationPayload(payload, 'create');
			return api.post<T>(url, data, endpoint);
		},
		onSuccess: (data: any, variables: MutationPayload) => handleSuccess(data, variables, 'Tạo thành công'),
	});

	const updateMutation = useMutation({
		mutationFn: (payload: MutationPayload) => {
			const { url, data } = parseMutationPayload(payload, 'update');
			return api.post<T>(url, data, endpoint);
		},
		onSuccess: (data: any, variables: MutationPayload) => {
			close();
			handleSuccess(data, variables, 'Cập nhật thành công');
		},
	});

	const deleteMutation = useMutation({
		mutationFn: (payload: MutationPayload) => {
			const { url, data, meta } = parseMutationPayload(payload, 'delete');
			return api.post<T>(url, data, meta._customEndpoint !== undefined ? meta._customEndpoint : endpoint);
		},
		onSuccess: (data: any, variables: MutationPayload) => {
			close();
			handleSuccess(data, variables, 'Xóa thành công');
		},
	});

	const create = () => createMutationSource;
	const update = () => updateMutation;
	const remove = () => deleteMutation;

	const updateConfirm = async (
		updateData: any, // Nên là MutationPayload
		options?: {
			title?: string;
			message?: string;
			_customUrl?: string;
			_customUrlSegment?: string;
			_customMessage?: string;
			_queryKey?: any[];
			_closeModal?: boolean;
			_hideMessage?: boolean;
			onSuccess?: (data: any) => void;
			onError?: (error: any) => void;
		},
	) => {
		const {
			title,
			message,
			_customUrl,
			_customUrlSegment,
			_closeModal,
			_hideMessage,
			_queryKey,
			onSuccess,
			onError,
		} = options || {};
		const defaultMessage = 'Bạn chắc chắn muốn cập nhật item này';
		const res = await confirm({
			title: title || 'Xác nhận cập nhật',
			html: message || defaultMessage,
			cancelButtonText: 'Hủy',
			confirmButtonText: 'Đồng ý',
			icon: 'warning',
		});
		if (res?.isConfirmed) {
			loading();
			updateMutation.mutate(
				{
					...updateData,
					_customUrl,
					_customUrlSegment,
					_closeModal,
					_hideMessage,
					_queryKey,
				},
				{
					onSuccess: (data) => {
						// Gọi callback onSuccess nếu được cung cấp
						onSuccess?.(data);
					},
					onError: (error) => {
						// Gọi callback onError nếu được cung cấp
						onError?.(error);
					},
				},
			);
			return true;
		}
		toast.info('Thao tác cập nhật đã được hủy.');
		return false;
	};

	const deleteConfirm = async (
		deleteData: any,
		options?: {
			title?: string;
			message?: string;
			_customUrl?: string;
			_customUrlSegment?: string;
			_customEndpoint?: string;
			_customMessage?: string;
			_queryKey?: any;
			_closeModal?: boolean;
			_hideMessage?: boolean;
			onSuccess?: (data: any) => void;
			onError?: (error: any) => void;
		},
	) => {
		// Thêm tùy chọn URL vào đây
		if (!deleteData?.id) {
			toast.error('Không tìm thấy item cần xóa');
			return Promise.resolve();
		}

		const defaultMessage = `Bạn chắc chắn muốn xóa ${
			deleteData?.name ? `"${deleteData?.name}"` : 'item này'
		}? \n Hành động này không thể hoàn tác.`;
		const res = await confirm({
			title: options?.title || 'Xác nhận xóa',
			html: options?.message || defaultMessage,
			cancelButtonText: 'Hủy',
			confirmButtonText: 'Đồng ý',
			icon: 'warning',
		});
		if (res?.isConfirmed) {
			loading();
			const { onSuccess, onError, ...mutationPayload } = options || {};
			deleteMutation.mutate(
				{ ...deleteData, ...mutationPayload },
				{
					onSuccess: (data) => {
						onSuccess?.(data);
					},
					onError: (error) => {
						onError?.(error);
					},
				},
			);
			return true;
		}
		toast.info('Thao tác xóa đã được hủy.');
		return false;
	};
	const uploadFile = async (file: File, path?: string) => {
		const formDataPost = new FormData();
		formDataPost.append('file', file);
		if (user?.code_key) {
			formDataPost.append(user?.code_key, 'key_code');
		}
		if (user?.user_token) {
			formDataPost.append(user?.user_token, 'token');
		}
		const urlRequest = resourcePath + (path || '');
		return await api.postForm(urlRequest, formDataPost, endpoint);
	};

	return {
		getAll,
		create,
		update,
		deleteConfirm,
		updateConfirm,
		getInfinite,
		getOne,
		remove,
		uploadFile,
	};
};

const formatUrl = (path: string, suffix?: string) => {
	if (!suffix) return path;
	const base = path.replace(/\/(list|log_list|list_all|dic)$/, '');
	return `${base.replace(/\/$/, '')}/${suffix.replace(/^\//, '')}`;
};
